/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Edit the tags on one activity, reusing whatever tags the library already has.
 * @state-owner local draft state; dataset store owns the saved tags
 * @allowed-imports React; dataset and workspace stores; shared components and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api activities module
 * @side-effects writes the tag list back to the activity
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useState } from 'react';
import { ModalDocument, SectionLabel } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import type { ActivityRecord } from '../../../shared/lib/model';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';

export function TagDialog({ activity }: { activity: ActivityRecord }) {
  const t = useTranslator();
  const { closeModal } = useOverlay();
  const activities = useDatasetStore((state) => state.activities);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const [tags, setTags] = useState<readonly string[]>(activity.tags);
  const [draft, setDraft] = useState('');

  /** Existing tags are offered first: a library with one spelling of a tag is worth more. */
  const known = [...new Set(activities.flatMap((item) => item.tags))].filter(
    (tag) => !tags.includes(tag),
  );

  const add = (tag: string) => {
    const value = tag.trim();
    if (!value || tags.includes(value)) return;
    setTags([...tags, value]);
    setDraft('');
  };

  const save = () => {
    useDatasetStore.getState().updateActivity(activity.id, { tags });
    closeModal();
    notify(tags.length ? `${t('已更新標籤')}：${tags.join('、')}` : t('已清空標籤'));
  };

  return (
    <ModalDocument
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button className="bt pri" type="button" onClick={save}>
            {t('儲存')}
          </button>
        </>
      }
      maxWidth={480}
      subtitle={activity.name}
      title={t('標籤')}
    >
      <SectionLabel first>{t('這份的標籤')}</SectionLabel>
      {tags.length ? (
        <div className="fbar" style={{ margin: '0 0 10px' }}>
          {tags.map((tag) => (
            <button
              className="ch on"
              key={tag}
              title={t('移除')}
              type="button"
              onClick={() => setTags(tags.filter((item) => item !== tag))}
            >
              {`${tag} ×`}
            </button>
          ))}
        </div>
      ) : (
        <p className="nlempty">{t('還沒有標籤。')}</p>
      )}
      <div className="ansrow" style={{ marginTop: 4 }}>
        <input
          aria-label={t('新標籤')}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') add(draft);
          }}
          placeholder={t('輸入後按 Enter')}
          style={{ flex: 1 }}
          value={draft}
        />
        <button className="bt" type="button" onClick={() => add(draft)}>
          {t('加入')}
        </button>
      </div>
      {known.length ? (
        <>
          <SectionLabel>{t('已經用過的標籤')}</SectionLabel>
          <div className="fbar" style={{ margin: 0 }}>
            {known.map((tag) => (
              <button className="ch" key={tag} type="button" onClick={() => add(tag)}>
                {tag}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </ModalDocument>
  );
}
